import { EmploymentField, useEmploymentSelection } from './EmploymentField'
import { useState } from 'react'
import { Text, XStack, YStack } from 'tamagui'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useApp } from '../../../i18n'
import { api } from '../../../api/client'
import { tone } from '../../../theme/tones'
import { Button, DateField, FormTextArea, Icon } from '../../../ui'
import { DATE_RE } from '../../../lib/date'
import { EDO_TYPE_LABEL } from '../../../lib/edo-labels'
import type { AbsenceOverlap, Profile } from '../../../lib/types'
import { RoutePreview } from '../components/RouteTimeline'
import { Field, FormActions, MISSING_DATE_FROM, MISSING_DATE_TO, MISSING_WORKPLACE, dateCol } from './shared'
import { OnBehalfField } from './OnBehalfField'

// ── Sick leave (больничный лист, волна 07.2026) ─────────────────────────────────
/**
 * Больничный: рук → HR (приказ) → REGISTERED. `overlaps` в ответе — мягкое предупреждение
 * (заявка создаётся всегда), поэтому success-обработчик решает сам: закрыть форму (нет
 * пересечений, как отгул) или показать список и ждать явного подтверждения.
 */
export function SickLeaveForm({ onDone }: { onDone: () => void }) {
  const { t } = useApp()
  const qc = useQueryClient()
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [comment, setComment] = useState('')
  const [overlaps, setOverlaps] = useState<AbsenceOverlap[]>([])
  // Подача «от имени» (роль proxy): выбранный сотрудник станет инициатором заявки на бэке.
  const [onBehalf, setOnBehalf] = useState<Profile | null>(null)
  const employment = useEmploymentSelection(onBehalf?.id)
  const create = useMutation({
    mutationFn: () =>
      api.createSickLeave({
        dateFrom,
        dateTo,
        comment: comment.trim() || undefined,
        onBehalfOfUserId: onBehalf?.id,
        employmentId: employment.employmentId,
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      if (res.overlaps?.length) setOverlaps(res.overlaps)
      else onDone()
    },
  })

  const datesOk = DATE_RE.test(dateFrom) && DATE_RE.test(dateTo) && dateTo >= dateFrom
  // Чего не хватает — теми же проверками, что держат кнопку (подсказка под ней).
  const missing = [
    !employment.ready && t(MISSING_WORKPLACE),
    !DATE_RE.test(dateFrom) && t(MISSING_DATE_FROM),
    !DATE_RE.test(dateTo) && t(MISSING_DATE_TO),
  ].filter((x): x is string => !!x)
  const ok = missing.length === 0 && datesOk && !create.isPending
  const submit = () => ok && create.mutate()

  // Заявка уже создана — пересечения показаны, ждём явного подтверждения вместо автозакрытия.
  if (overlaps.length > 0) {
    return (
      <YStack gap={14}>
        <YStack gap={8} backgroundColor={tone('gold').soft} borderWidth={1} borderColor={tone('gold').solid} borderRadius={7} padding={12}>
          <XStack alignItems="center" gap={8}>
            <Icon name="triangle-alert" size={16} color={tone('gold').text} />
            <Text fontSize={13} fontWeight="700" color={tone('gold').text}>
              {t({ ru: 'Заявка создана. Пересекается с другими отсутствиями:', en: 'Request created. Overlaps with other absences:' })}
            </Text>
          </XStack>
          {overlaps.map((o) => (
            <Text key={o.id} fontSize={12} color="$muted">
              {`${EDO_TYPE_LABEL[o.type]} #${o.number} (${o.from.slice(0, 10)}–${o.to.slice(0, 10)})`}
            </Text>
          ))}
        </YStack>
        <Button variant="primary" block onPress={onDone}>
          {t({ ru: 'Понятно', en: 'Got it' })}
        </Button>
      </YStack>
    )
  }

  return (
    <YStack gap={14}>
      <OnBehalfField value={onBehalf} onChange={setOnBehalf} />
      <EmploymentField selection={employment} />
      <XStack gap={12} flexWrap="wrap">
        <YStack {...dateCol} gap={6}>
          <Field label={t({ ru: 'С', en: 'From' })} required>
            <DateField
              value={dateFrom}
              onChange={(d) => {
                setDateFrom(d)
                // Keep the range valid: drop an end that now precedes the new start.
                if (dateTo && dateTo < d) setDateTo('')
              }}
              placeholder="ГГГГ-ММ-ДД"
              highlight={!dateFrom}
            />
          </Field>
        </YStack>
        <YStack {...dateCol} gap={6}>
          <Field label={t({ ru: 'По', en: 'To' })} required>
            <DateField
              value={dateTo}
              onChange={setDateTo}
              min={dateFrom || undefined}
              placeholder="ГГГГ-ММ-ДД"
              highlight={!dateTo}
            />
          </Field>
        </YStack>
      </XStack>
      <Field label={t({ ru: 'Комментарий (необязательно)', en: 'Comment (optional)' })}>
        <FormTextArea
          value={comment}
          onChangeText={setComment}
          maxLength={500}
          placeholder={t({ ru: 'Например: ОРВИ', en: 'e.g. flu' })}
        />
      </Field>
      <RoutePreview type="SICK_LEAVE" onBehalfOfUserId={onBehalf?.id} employmentId={employment.employmentId} enabled={employment.ready} />
      <FormActions onCancel={onDone} onSubmit={submit} ok={ok} m={create} missing={missing} />
    </YStack>
  )
}
